import { createContext, useEffect, useContext } from "react";
import {
  briefGalleryList,
  briefGalleryListForEventTab,
} from "../../v2_actions/brief_gallery";
import { galleryEventListForEventTab } from "../../v2_actions/gallery_event";
import { albumListForEventTab } from "../../v2_actions/customer_album";
import { useDispatch } from "react-redux";
import { ItemList } from "../../orm";
import { makeLogger } from "../../utils/logger";

const logger = makeLogger({
  enabled: true,
  label: "useEventPageContext",
  color: "orange",
});

export const EventPageContext = createContext(null);

const GALLERY_EVENT_PAGE_SIZE = 16;
const ALBUM_PAGE_SIZE = 90;
const BRIEF_GALLERY_PAGE_SIZE = 90;

interface ListFilterArgs extends Record<string, string> {
  gallery_type: "event";
  photographers: string;
  _callSource: string;
}

/**
 * Shared util to centralise updates to event/album/galleries. This marshals
 * updates to prevent duplicate calls / prevent calls where filters do not change
 * @returns
 */
export const useEventPageContext = () => {
  const dispatch = useDispatch();
  const context = useContext(EventPageContext);

  // set paging sizes on load
  useEffect(() => {
    dispatch(
      galleryEventListForEventTab.updatePaginationNumItemsPerPage(
        GALLERY_EVENT_PAGE_SIZE,
      ),
    );
    dispatch(
      albumListForEventTab.updatePaginationNumItemsPerPage(ALBUM_PAGE_SIZE),
    );
    dispatch(
      briefGalleryListForEventTab.updatePaginationNumItemsPerPage(
        BRIEF_GALLERY_PAGE_SIZE,
      ),
    );
  }, []);

  const checkFilterAndMaybeFetch = (
    list: ItemList,
    filterArgs: ListFilterArgs,
  ) => {
    const currentFilter = list.getFilter();
    const { _callSource, ...filterArgsPatch } = filterArgs;

    let filterChange = false;

    // figure out if the filterArgs should cause a fetch
    Object.keys(filterArgsPatch).forEach((filterArgKey) => {
      const filterArg = filterArgsPatch[filterArgKey];
      logger.log(_callSource, currentFilter[filterArgKey], filterArg);
      // arg already exists
      if (currentFilter[filterArgKey] === undefined) {
        // don't trigger fetch when filter is initialised (undefined)
        // first call from
      } else if (currentFilter[filterArgKey]) {
        if (currentFilter[filterArgKey] !== filterArg) {
          filterChange = true;
        }
        // arg is new
      } else {
        // only trigger fetch for non falsy change
        if (filterArg != null && filterArg != "") {
          filterChange = true;
        }
      }
    });

    // update filter args even if we don't fetch
    dispatch(list.updateListFilter(filterArgsPatch));

    if (filterChange) {
      logger.log("FETCHING", _callSource, { currentFilter, filterArgsPatch });
      dispatch(
        list.fetchListIfNeeded({
          callSource: _callSource,
          forceUpdate: undefined,
        }),
      );
    }
  };

  const galleryFilter = briefGalleryListForEventTab.getFilter();
  const hasActiveFilters = [
    "activity_tags",
    "shot_at_date_to",
    "shot_at_date_from",
    "gallery_event",
    "photographers",
  ].some((filter) => !!galleryFilter[filter]);

  return {
    hasActiveFilters,
    updateListFilter: (filterArgs: ListFilterArgs) => {
      checkFilterAndMaybeFetch(galleryEventListForEventTab, filterArgs);
      checkFilterAndMaybeFetch(briefGalleryListForEventTab, filterArgs);
      checkFilterAndMaybeFetch(albumListForEventTab, filterArgs);
    },
  };
};
